import { FC, useMemo, useCallback, useEffect, useState } from 'react';
import { useTranslation, TFunction } from 'react-i18next';
import { Chat } from 'ts-components/Chat';
import { QuickResponseData } from 'ts-components/QuickResponses';
import { useChat } from './ChatBotV2Context';
import { ChatBotV2Provider } from './ChatBotV2Context';
import { AIChatContainerProps, AIChatProps } from './types';
import { ADD_MORE_DETAILS_OPTION, SESSION_STORAGE_KEY } from './constants';
import { useKizenAiFrameMessage } from 'pages/AIChatEmbed/useKizenAiFrameMessage';
import { useSessionStorage } from 'hooks/useSessionStorage';

const CUSTOM_OBJECT_EDITOR_GRAPH = 'CustomObjectEditorGraph';
const CUSTOM_OBJECT_EDITOR_TAG = 'latest';

const CHAT_BUTTONS = {
  START_OVER: (t: TFunction) => {
    return { label: t('New Chat'), value: 'START_OVER' } as const;
  },
};

export const ChatBotV2Container: FC<AIChatContainerProps> = ({
  header,
  showLogo = false,
  headerLogoUrl = null,
  renderMarkdown = true,
  hideRefresh = false,
  hideQuickActions = false,
  sampleIdeas,
  enableQuickActionButtons = false,
  alwaysShowStartOver = false,
  additionalActions,
  onActionSelect,
  onCreateNewChat,
  headerMenuOptions,
  onHeaderMenuOptionSelect,
  onReady,
  onCopyMessage,
  onLikeMessage,
  onDislikeMessage,
  onRefreshMessage,
  onPreviewNavigate,
}) => {
  const { t } = useTranslation();
  const {
    messages,
    quickResponseData,
    placeholder,
    sendMessage,
    handleQuickResponseSelect,
    isStreaming,
    isLoadingHistory,
    connectionState,
    resetChat,
    alert,
  } = useChat();

  const [selectedQuickResponses, setSelectedQuickResponses] = useState<
    { id: string; value: string }[]
  >([]);
  const [shouldFocusInput, setShouldFocusInput] = useState(false);

  // Expose resetChat to parent
  useEffect(() => {
    if (onReady) {
      onReady({ resetChat });
    }
  }, [onReady, resetChat]);

  const actionQuickResponseData: QuickResponseData = useMemo(() => {
    if (!enableQuickActionButtons) {
      return { options: [], selectType: 'action' };
    }

    const actions = [];

    if (messages.length > 1 || alwaysShowStartOver || alert) {
      actions.push(CHAT_BUTTONS.START_OVER(t));
      if (additionalActions?.length) {
        actions.push(...additionalActions);
      }
    }

    return { options: actions, selectType: 'action' };
  }, [
    enableQuickActionButtons,
    messages.length,
    alwaysShowStartOver,
    alert,
    additionalActions,
    t,
  ]);

  const finalQuickResponseData: QuickResponseData = useMemo(() => {
    if (
      hideQuickActions ||
      connectionState === 'connecting' ||
      isLoadingHistory
    ) {
      return { options: [], selectType: 'action' };
    }

    if (
      enableQuickActionButtons &&
      actionQuickResponseData.options.length > 0 &&
      quickResponseData.options.length === 0
    ) {
      return actionQuickResponseData;
    }

    if (messages.length <= 1 && sampleIdeas && sampleIdeas.options.length > 0) {
      return sampleIdeas;
    }

    if (
      quickResponseData.selectType === 'multi_select' &&
      quickResponseData.options.length > 0
    ) {
      return {
        options: [
          ...quickResponseData.options,
          { label: ADD_MORE_DETAILS_OPTION, value: ADD_MORE_DETAILS_OPTION },
        ],
        selectType: 'multi_select',
      };
    }

    return quickResponseData;
  }, [
    hideQuickActions,
    connectionState,
    isLoadingHistory,
    enableQuickActionButtons,
    actionQuickResponseData,
    quickResponseData,
    messages.length,
    sampleIdeas,
  ]);

  const quickResponsesHeader = useMemo(() => {
    if (finalQuickResponseData.options.length === 0) {
      return '';
    }

    if (
      enableQuickActionButtons &&
      actionQuickResponseData.options.length > 0 &&
      quickResponseData.options.length === 0
    ) {
      return t('ACTIONS');
    }

    if (
      messages.length <= 1 &&
      sampleIdeas &&
      sampleIdeas.options.length > 0 &&
      !hideQuickActions
    ) {
      return t('SAMPLE IDEAS (CHOOSE ONE OR TYPE YOUR OWN)');
    }

    if (
      quickResponseData.options.length > 0 &&
      quickResponseData.selectType === 'single_select'
    ) {
      return t('QUICK RESPONSES (CHOOSE ONE OR TYPE YOUR OWN)');
    }

    if (
      quickResponseData.options.length > 0 &&
      quickResponseData.selectType === 'multi_select'
    ) {
      return t('QUICK RESPONSES (CHOOSE MULTIPLE OR TYPE YOUR OWN)');
    }

    return '';
  }, [
    finalQuickResponseData.options.length,
    enableQuickActionButtons,
    actionQuickResponseData.options.length,
    quickResponseData,
    messages.length,
    sampleIdeas,
    hideQuickActions,
    t,
  ]);

  const handleQuickResponseSelectWithActions = useCallback(
    (value: string, type: string, url?: string) => {
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
        return;
      }

      if (type === 'action' && value === 'START_OVER') {
        resetChat();
        return;
      }

      if (type === 'action') {
        onActionSelect?.(value);
        return;
      }

      if (type === 'multi_select') {
        const alreadySelected = selectedQuickResponses.find(
          (p) => p.value === value
        );
        if (alreadySelected) {
          setSelectedQuickResponses((prev) =>
            prev.filter((p) => p.value !== value)
          );
          if (value === ADD_MORE_DETAILS_OPTION) setShouldFocusInput(false);
          return;
        }
        if (value === ADD_MORE_DETAILS_OPTION) setShouldFocusInput(true);
        setSelectedQuickResponses((prev) => [
          ...prev,
          { id: crypto.randomUUID(), value },
        ]);
        return;
      }

      handleQuickResponseSelect(value, type, url);
    },
    [
      handleQuickResponseSelect,
      resetChat,
      selectedQuickResponses,
      onActionSelect,
    ]
  );

  const handleSendMessage = useCallback(
    (message: string) => {
      const addMoreDetailsSelected = selectedQuickResponses.find(
        (pill) => pill.value === ADD_MORE_DETAILS_OPTION
      );

      if (selectedQuickResponses.length > 0) {
        const selectedPills = selectedQuickResponses.filter(
          (pill) => pill.value !== ADD_MORE_DETAILS_OPTION
        );

        if (message.trim() !== '' && addMoreDetailsSelected) {
          selectedPills.push({ id: 'userMessage', value: message });
        }

        // Format selected pills into a comma-separated string with 'and' before the last item
        const formattedMessage = selectedPills
          .map((pill) => pill.value)
          .reduce((acc, curr, index, array) => {
            if (index === 0 && array.length === 1) return curr;
            if (index === 0) return `"${curr}"`;
            if (index === array.length - 1) return `${acc} and "${curr}"`;
            return `${acc}, "${curr}"`;
          }, '');

        sendMessage(formattedMessage);
        setSelectedQuickResponses([]);
        setShouldFocusInput(false);
        return;
      }

      if (!message.trim()) return;
      sendMessage(message);
    },
    [sendMessage, selectedQuickResponses]
  );

  // Clear selections when new message arrives
  useEffect(() => {
    if (messages.length > 0 && isStreaming) {
      setSelectedQuickResponses([]);
      setShouldFocusInput(false);
    }
  }, [messages.length, isStreaming]);

  const handleAlert = useCallback(() => {}, []);

  const isLoadingNewSession =
    connectionState === 'connecting' || isLoadingHistory;

  return (
    <Chat
      header={header}
      showLogo={showLogo}
      headerLogoUrl={headerLogoUrl}
      messages={messages}
      quickResponseData={finalQuickResponseData}
      quickResponsesHeader={quickResponsesHeader}
      selectedQuickResponses={selectedQuickResponses}
      placeholder={placeholder}
      onSendMessage={handleSendMessage}
      onQuickResponseSelect={handleQuickResponseSelectWithActions}
      isMessageResponseLoading={isStreaming}
      isLoadingNewSession={isLoadingNewSession}
      isCompleteSessionLoading={false}
      disableSendMessageButton={isStreaming || isLoadingNewSession}
      renderMarkdown={renderMarkdown}
      onCreateNewChat={(context) => {
        if (context?.graphName) {
          resetChat();
          onCreateNewChat?.(context);
        } else {
          onCreateNewChat?.(context);
        }
      }}
      onAlert={handleAlert}
      onCompleteSession={() => {}}
      alert={alert}
      hideRefresh={hideRefresh}
      hideControllerButtons={enableQuickActionButtons}
      headerMenuOptions={headerMenuOptions}
      handleHeaderMenuOptionSelect={onHeaderMenuOptionSelect}
      onCopyMessage={onCopyMessage}
      onLikeMessage={onLikeMessage}
      onDislikeMessage={onDislikeMessage}
      onRefreshMessage={onRefreshMessage}
      shouldFocusInput={shouldFocusInput}
      onPreviewSelect={onPreviewNavigate}
    />
  );
};

export const ChatBotV2: FC<AIChatProps> = ({
  config: initialConfig,
  sessionId,
  autoConnect = true,
  initialMessage,
  freshSession = false,
  onError,
  onMessageComplete,
  onSessionCreated,
  onCreateNewChat: onCreateNewChatProp,
  ...containerProps
}) => {
  const [config, setConfig] = useState(initialConfig);
  const [configKey, setConfigKey] = useState(0);
  const { removeItem } = useSessionStorage();

  const handleCreateNewChat = useCallback(
    (context?: Record<string, unknown>) => {
      if (context?.graphName) {
        const graphName = context.graphName as string;
        const graphTag = context.graphTag as string | undefined;
        removeItem(SESSION_STORAGE_KEY);
        setConfig({
          graph: graphName,
          tag:
            graphName === CUSTOM_OBJECT_EDITOR_GRAPH
              ? CUSTOM_OBJECT_EDITOR_TAG
              : graphTag || config.tag,
        });
        setConfigKey((k) => k + 1);
      }
      onCreateNewChatProp?.(context);
    },
    [onCreateNewChatProp, removeItem, config.tag]
  );

  useKizenAiFrameMessage(
    (data) => {
      if (data.action === 'changeGraphConfig') {
        const graphName = (data.graphName as string) || config.graph;
        const graphTag = data.graphTag as string | undefined;
        removeItem(SESSION_STORAGE_KEY);
        setConfig({
          graph: graphName,
          tag:
            graphName === CUSTOM_OBJECT_EDITOR_GRAPH
              ? CUSTOM_OBJECT_EDITOR_TAG
              : graphTag || config.tag,
        });
        setConfigKey((k) => k + 1);
      }
    },
    { iframeOnly: true }
  );

  return (
    <ChatBotV2Provider
      key={configKey}
      config={config}
      sessionId={sessionId}
      autoConnect={autoConnect}
      initialMessage={initialMessage}
      freshSession={freshSession}
      onError={onError}
      onMessageComplete={onMessageComplete}
      onSessionCreated={onSessionCreated}
    >
      <ChatBotV2Container
        {...containerProps}
        onCreateNewChat={handleCreateNewChat}
      />
    </ChatBotV2Provider>
  );
};
